import {
  EmptyArray,
  arrayToMap,
  arrayToObject,
  groupBy,
  min,
  nonNullable,
  partitionBy,
  sortBy,
} from 'lib/array/arrayUtils'
import { buildRoomPackageOptionsViewOrderLPC } from 'lib/offer/offerPageUtil'
import { isHotelPackage } from 'lib/offer/offerTypes'
import { coalesce } from 'lib/object/objectUtils'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import { logNewRelic } from 'services/newRelic'

export type PropertyRoomsMapping = Record<string, App.HotelPackage | App.BedbankPackage>

export interface RoomPackageOptions {
  roomId: string
  // TODO(HP-1118): Switch to unified `PropertyRoom` type.
  propertyRoom: App.HotelPackage | App.BedbankPackage
  lpc: Array<App.OfferPackageOptionView>
  bedbank: Array<App.BedbankOfferPackageView>
}

export interface RoomRateViews {
  roomId: string
  // TODO(HP-1118): Switch to unified `PropertyRoom` type.
  propertyRoom: App.HotelPackage | App.BedbankPackage
  availableRooms: number | undefined
  displayRates: Array<RateView>
  roomFitsCapacity: boolean
  datesAvailable: boolean
  isSelected: boolean
  isHighlighted: boolean
  roomRateUnmatched: boolean
}

interface BaseRateView {
  price: number
  boardCode: App.RateBoardCode
  refundable: boolean
}

/**
 * @deprecated This will be removed when we discontinue the disabled variant of the
 * `spp_use_accom_content` feature flag.
 */
interface LpcRateView extends BaseRateView {
  type: 'LPC'
  lpcRate: App.OfferPackageOptionView
  propertyRate: undefined
}

interface PropertyRateView extends BaseRateView {
  type: 'property'
  lpcRate: undefined
  propertyRate: App.PropertyRate
}

export type RateView = LpcRateView | PropertyRateView

function lpcToCombinedRate(rate: App.OfferPackageOptionView): RateView {
  return {
    price: rate.memberPricing?.price || rate.pricing?.price,
    boardCode: rate.package.boardCode ?? 'LE',
    refundable: rate.refundable,
    type: 'LPC',
    lpcRate: rate,
    propertyRate: undefined,
  }
}

function toSortedRates(
  rates: Array<RateView>,
  highlightedOption: App.OfferPackageOptionView | undefined,
): Array<RateView> {
  const sortedRates = sortBy(rates, (rate) => rate.price || Number.MAX_SAFE_INTEGER, 'asc')
  if (!highlightedOption) return sortedRates
  return [
    lpcToCombinedRate(highlightedOption),
    ...sortedRates.filter(({ lpcRate }) => lpcRate !== highlightedOption),
  ]
}

/**
 * @deprecated This will be removed when we discontinue the disabled variant of the
 * `spp_use_accom_content` feature flag.
 */
export function getCombinedRates(
  availableOptions: Array<App.OfferPackageOptionView>,
  options: {
    roomRateId?: string
    isHighlighted?: boolean
    previewMode?: boolean
    utmContent?: string | null
  }): Array<RateView> {
  const { isHighlighted, previewMode, roomRateId, utmContent } = options

  let highlightedOption: App.OfferPackageOptionView | undefined
  if (isHighlighted && roomRateId && availableOptions.length >= 1) {
    // Pluck the highlighted LPC rate so we can move it to the top of the sorted list of options
    // just before we return.
    highlightedOption = availableOptions.find((view) => view.package.roomRate?.id === roomRateId)
    if (!highlightedOption && utmContent === 'gha-lpc') {
      // If `roomRateId` is associated with some currency, remove the currency and extract the rate
      // ID for comparison (e.g. stripping `_USD` from `cb4ebd70-33d1-11ed-9609-ffb70b8fec99_USD`).
      const baseRateId = roomRateId?.split('_')[0]
      highlightedOption = availableOptions.find((view) => view.package.roomRate?.id === baseRateId)
    }
  }

  // now we combine our LPC and bedbank rates
  const allRates: Array<RateView> = availableOptions.map(lpcToCombinedRate)

  if (previewMode) {
    // preview mode on property pages doesn't include bedbank rates and doesn't do any
    // any extra de-duping. Preview users want to see *all* (LPC) rates
    // those without a price of 0 will be the placed at the end
    return toSortedRates(allRates, highlightedOption)
  }

  // now dedupe them based on board code
  const ratesByCode = groupBy(allRates, rate => rate.boardCode ?? 'LE')
  const deDupedRates = Array.from(ratesByCode.entries()).flatMap(([boardCode, rateGroup]) => {
    if (boardCode === 'LE') {
      // this group is already unique, just pass them on
      return rateGroup
    }

    // now group again by cancellation policy
    const [refundableRates, nonRefundableRates] = partitionBy(rateGroup, group => group.refundable)
    // board codes describe common sets of inclusions, same board code = basically the same rate
    // so just return the cheapest one. In the event we have LPC and bedbank the same price
    // use the LPC one instead until we have margin info
    return nonNullable([
      min(sortBy(refundableRates, rate => rate.type === 'LPC' ? 1 : 0, 'desc'), rate => rate.price)!,
      min(sortBy(nonRefundableRates, rate => rate.type === 'LPC' ? 1 : 0, 'desc'), rate => rate.price)!,
    ])
  })

  return toSortedRates(deDupedRates, highlightedOption)
}

export function getPackageOptionsByRoom(
  packageOptions: Array<App.OfferPackageOptionView>,
  bedbankPackageViews: Array<App.BedbankOfferPackageView>,
  mapping: App.PropertyOfferMapping | undefined,
  lpcRooms: PropertyRoomsMapping,
  bedbankRooms: PropertyRoomsMapping,
): Array<RoomPackageOptions> {
  // Prepare LPC and Bedbank package options by room
  const lpcPackagesByOpaqueRoomId = arrayToMap(
    buildRoomPackageOptionsViewOrderLPC(packageOptions), pkg => pkg.roomId, pkg => pkg.packageViews,
  )
  const bedbankRoomIdToOpaqueRoomId = new Map(Object.values(mapping?.rooms ?? {}).flatMap(
    ({ lpcRoomIds, bedbankRoomIds }) => {
      // If there are a couple of reservation room IDs, it's likely that one of them is for a flash
      // offer, which we can ignore.
      const lpcRoomId = lpcRoomIds.find(id => lpcPackagesByOpaqueRoomId.has(id))
      if (!lpcRoomId) return []
      return bedbankRoomIds.map((bedbankRoomId) => [bedbankRoomId, lpcRoomId])
    },
  ))
  const bedbankPackagesByOpaqueRoomId = groupBy(
    bedbankPackageViews,
    // Until recently, `mapping` (supplied by `svc-accommodation`) _only_ included bedbank rooms
    // (mapped to an accommodation room) if there was also a reservation room mapped to that
    // accommodation room. However, we _didn't_ want to filter out bedbank rates just because there
    // wasn't an equivalent reservation room.
    //
    // Long-term, we should replace references to reservation/bedbank room IDs with accommodation
    // room IDs. In the interim, we'll pretend they're interchangable; as they're both UUIDs, we
    // needn't worry about collisions.
    view => bedbankRoomIdToOpaqueRoomId.get(view.package.roomId) ?? view.package.roomId,
  )

  // Compute set of rooms with any available package options
  const opaqueRoomIds = new Set([
    ...lpcPackagesByOpaqueRoomId.keys(),
    ...bedbankPackagesByOpaqueRoomId.keys(),
  ])

  // Combine LPC and Bedbank package options by room
  const droppedOpaqueRoomIds = new Set()
  const views = [...opaqueRoomIds].flatMap((opaqueRoomId) => {
    const lpcPackages = lpcPackagesByOpaqueRoomId.get(opaqueRoomId) ?? []
    const bedbankPackages = bedbankPackagesByOpaqueRoomId.get(opaqueRoomId) ?? []
    const hasRates = lpcPackages.length || bedbankPackages.some(pkg => pkg.available)
    const propertyRoom = lpcRooms[opaqueRoomId] ?? bedbankRooms[opaqueRoomId]
    if (!propertyRoom) {
      // This is probably a reservation/bedbank room that isn't mapped to an accommodation room.
      if (hasRates) droppedOpaqueRoomIds.add(opaqueRoomId)
      return []
    }

    if (hasRates) {
      return {
        // For now, `roomId` needs to be a reservation room ID (falling back to a bedbank room ID)
        // for downstream checks (like GHA-highlighted rates).
        roomId: opaqueRoomId,
        propertyRoom,
        lpc: lpcPackages,
        bedbank: bedbankPackages,
      }
    }

    return []
  })
  if (droppedOpaqueRoomIds.size) {
    // Log so we can initially sense check grossly misconfigured accommodation service properties.
    // Batch log to make combing through Sentry easier.
    logNewRelic('Dropping rates for unexpected opaque room ID(s)', {
      accommodationPropertyId: mapping?.propertyId,
      droppedOpaqueRoomIds: JSON.stringify([...droppedOpaqueRoomIds].sort()),
    }, 'warn')
  }

  // Property page just always sorts rooms by best price
  return sortBy(views, view => {
    const allPrices = [
      ...view.lpc.filter(lpc => lpc.capacityValid && lpc.hotelAvailable && !!lpc.hasAvailableRates).map(lpc => lpc.memberPricing?.price || lpc.pricing.price || Number.MAX_SAFE_INTEGER),
      ...view.bedbank.filter(bedbank => bedbank.capacityValid && bedbank.available).map(bedbank => bedbank.cheapestRate?.totals.memberInclusive || bedbank.cheapestRate?.totals.inclusive || Number.MAX_SAFE_INTEGER),
      Number.MAX_SAFE_INTEGER,
    ]

    return min(allPrices)
  }, 'asc')
}

const emptyRoomsMappings: [PropertyRoomsMapping, PropertyRoomsMapping, PropertyRoomsMapping] =
  [{}, {}, {}]

export function getRoomsMappings(
  basePackageViews: Array<App.OfferPackageOptionView>,
  bedbankPackages: Array<App.BedbankPackage> | undefined,
  roomMappings: App.PropertyOfferMapping['rooms'] | undefined,
): [PropertyRoomsMapping, PropertyRoomsMapping, PropertyRoomsMapping] {
  if (!roomMappings) return emptyRoomsMappings

  const lpcRoomPackages = arrayToObject(
    basePackageViews,
    ({ package: pkg }) => pkg.roomType.id,
    ({ package: pkg }) => pkg,
  )
  const bedbankRoomPackages = arrayToObject(
    bedbankPackages ?? [],
    ({ roomId }) => roomId,
  )

  const rooms: PropertyRoomsMapping = {}
  const lpcRooms: PropertyRoomsMapping = {}
  const bedbankRooms: PropertyRoomsMapping = {}
  for (const { roomId, lpcRoomIds, bedbankRoomIds } of Object.values(roomMappings)) {
    // NB: `HotelPackage`s hold a lot of non-room-specific data (like pricing), but our instance
    // should _only_ be used for displaying room information.
    const room =
      coalesce(lpcRoomPackages, lpcRoomIds) ??
      coalesce(bedbankRoomPackages, bedbankRoomIds)
    if (!room) continue

    rooms[roomId] = room
    for (const id of lpcRoomIds) {
      lpcRooms[id] = room
    }
    for (const id of bedbankRoomIds) {
      bedbankRooms[id] = room
    }
  }
  return [rooms, lpcRooms, bedbankRooms]
}

export function getRatesByRoom(
  mapping: App.PropertyOfferMapping,
  propertyRooms: PropertyRoomsMapping,
  ratesByRoom: Array<App.PropertyRoomRates>,
  flights: boolean,
  selectedOpaqueRoomId: string | undefined,
  highlightedRoomId: string | undefined,
  highlightedRateId: string | undefined,
): Array<RoomRateViews> {
  /** Map from accommodation service room UUIDs (for LPC rooms) to their default order */
  const defaultOrderByRequiredRoomIds =
    Object.fromEntries(Object.entries(propertyRooms)
      .filter(([_, pkg]) => isHotelPackage(pkg))
      .map(([roomId]) => [roomId, mapping.rooms[roomId].defaultOrder]))

  const droppedRoomIds = new Set()
  const combinedRatesByRoom: Array<RoomRateViews> = ratesByRoom.flatMap((roomRates, roomIndex) => {
    const { roomId } = roomRates
    delete defaultOrderByRequiredRoomIds[roomId]
    if (!Object.hasOwn(propertyRooms, roomId)) {
      droppedRoomIds.add(roomId)
      return [] as Array<RoomRateViews>
    }

    const propertyRoom = propertyRooms[roomId]
    const opaqueRoomId = isHotelPackage(propertyRoom) ? propertyRoom.roomType.id : propertyRoom.roomId
    const isSelected = opaqueRoomId === selectedOpaqueRoomId
    const partialRoomRateViews = {
      roomId: opaqueRoomId,
      propertyRoom,
      isSelected,
    }

    // TODO(Xiao): Clean up.
    if ('unavailableReason' in roomRates) {
      if (roomRates.unavailableReason === 'DATE_UNAVAILABLE') {
        return [{
          ...partialRoomRateViews,
          availableRooms: undefined,
          displayRates: EmptyArray,
          roomFitsCapacity: false,
          datesAvailable: false,
          isHighlighted: false,
          roomRateUnmatched: true,
        }]
      } else if (roomRates.unavailableReason === 'EXCEEDS_ROOM_CAPACITY') {
        return [{
          ...partialRoomRateViews,
          availableRooms: undefined,
          displayRates: EmptyArray,
          roomFitsCapacity: false,
          datesAvailable: true,
          isHighlighted: false,
          roomRateUnmatched: true,
        }]
      } else {
        assertUnreachable(roomRates.unavailableReason)
      }
    }

    // TODO(Xiao): Clean up.
    const includedGuestsExceededPolicy = isHotelPackage(propertyRoom) ? propertyRoom.roomPolicyDescription : undefined
    const rates = flights ? roomRates.hotelWithFlightsRates : roomRates.hotelOnlyRates
    const displayRates: Array<PropertyRateView> = rates.map(propertyRate => {
      const updatedPropertyRate = propertyRate.exceedsIncludedGuests ? {
        ...propertyRate,
        includedGuestsExceededPolicy,
      } : propertyRate
      return {
        price: propertyRate.luxPlusTotals?.displayPrice ?? propertyRate.totals.displayPrice,
        boardCode: 'roomonly', // TOOD(Xiao): Implement stub.
        refundable: false, // TOOD(Xiao): Implement stub.
        type: 'property',
        lpcRate: undefined,
        propertyRate: updatedPropertyRate,
      }
    })
    const ratesAvailable = !!rates.length
    const isHighlighted = roomIndex === 0 && partialRoomRateViews.roomId === highlightedRoomId
    const roomRateUnmatched =
      !isHighlighted ||
      !rates[0] ||
      // For now, we need only worry about GHA LPC.
      !('lpcRateId' in rates[0]) ||
      // Accommodation service will move the highlighted rate to the start of the list for us.
      (rates[0].lpcRateId !== highlightedRateId)

    return [{
      ...partialRoomRateViews,
      availableRooms: undefined, // TODO(Xiao): Implement stub.
      displayRates,
      roomFitsCapacity: ratesAvailable,
      datesAvailable: ratesAvailable,
      isHighlighted,
      roomRateUnmatched,
    }]
  })
  if (droppedRoomIds.size) {
    // Races notwithstanding, this should never happen.
    logNewRelic('Dropping rates for unexpected accommodation room ID(s)', {
      accommodationPropertyId: mapping.propertyId,
      droppedAccommodationRoomIds: JSON.stringify([...droppedRoomIds].sort()),
    }, 'warn')
  }

  // TODO(HP-1196): Remove workaround when accommodation service reliably returns
  // `unavailableReason`s for all LPC rooms.
  const missingRequiredRoomIds = sortBy(
    Object.entries(defaultOrderByRequiredRoomIds),
    ([_, defaultOrder]) => defaultOrder,
    'asc',
  ).map(([roomId]) => roomId)
  for (const roomId of missingRequiredRoomIds) {
    const propertyRoom = propertyRooms[roomId]
    // This is invariably going to be a `HotelPackage`, but the type system can't be sure.
    const opaqueRoomId = isHotelPackage(propertyRoom) ? propertyRoom.roomType.id : propertyRoom.roomId
    const isSelected = opaqueRoomId === selectedOpaqueRoomId
    combinedRatesByRoom.push({
      roomId: opaqueRoomId,
      propertyRoom,
      availableRooms: undefined,
      displayRates: EmptyArray,
      roomFitsCapacity: false,
      datesAvailable: false,
      isSelected,
      isHighlighted: false,
      roomRateUnmatched: true,
    })
  }

  return combinedRatesByRoom
}
